import {
  AggregationTypes,
  type IndoorClimateScoresQuery,
  ScoreType,
  useIndoorClimateScoresQuery,
} from '@/__generated__/types';
import type { DashboardPeriod } from '@/constants/dashboard-period';

import { UNRESOLVED_SCOPE } from '../constants';
import type { IndoorClimateScore, SpaceScope } from '../types/home-types';

export interface UseIndoorClimateScoresParams {
  scope?: SpaceScope;
  timeSpan: DashboardPeriod;
}

type ScoreData =
  IndoorClimateScoresQuery['spacesStatistics'][number]['scoreData'];

const scoreOf = (
  scoreData: ScoreData,
  scoreType: ScoreType,
): IndoorClimateScore | undefined => {
  const item = scoreData.find((score) => score.scoreType === scoreType);
  if (!item?.aggregatedDataScoreInfo) return undefined;
  return {
    ...item.aggregatedDataScoreInfo,
    average: item.average,
    unit: item.scoreThresholdInfoSet?.displayUnit ?? null,
  };
};

export const useIndoorClimateScores = ({
  scope,
  timeSpan,
}: UseIndoorClimateScoresParams) => {
  const { data, loading, error, refetch } = useIndoorClimateScoresQuery({
    skip: !scope,
    variables: {
      ...(scope ?? UNRESOLVED_SCOPE),
      dataFilters: {
        timeSpan,
        readUntilNow: true,
        aggregationType: AggregationTypes.Avg,
      },
    },
    errorPolicy: 'all',
  });

  const statistics = data?.spacesStatistics ?? [];
  const scoreData =
    (
      statistics.find((item) => item.space.id === scope?.spaceId) ??
      statistics[0]
    )?.scoreData ?? [];

  return {
    scores: {
      temperature: scoreOf(scoreData, ScoreType.Temperature),
      co2: scoreOf(scoreData, ScoreType.Co2),
      humidity: scoreOf(scoreData, ScoreType.Humidity),
    },
    isLoading: loading || !scope,
    isError: !!error,
    isEmpty: !loading && scoreData.length === 0,
    retry: () => {
      void refetch();
    },
  };
};
